import { createRequisition, createRequisitionComment, submitRequisition, updateRequisition } from "../api/requisitions";
import type { OfflineQueueRecord } from "../types/requisition";
import { getDraft } from "./indexedDB/draftStore";
import { getQueuedIntent, listQueuedIntents, updateQueuedIntent } from "./indexedDB/queueStore";
import { rewriteOfflineRequisitionServerReference } from "./indexedDB/schema";
import { recordLastSyncedAt } from "./indexedDB/syncMetadata";
import { normalizeUuid, OFFLINE_UUID_MIGRATION_CONFLICT } from "./indexedDB/uuidCompatibility";

const REQUISITION_REJECTED_CONFLICT = "REQUISITION_REJECTED_CONFLICT";
const REQUISITION_REJECTED_MESSAGE = "This requisition was rejected — duplicate to retry.";

interface ReplayResult {
  processedCount: number;
  failedIntentId: string | null;
}

interface ReplayError {
  code: string | null;
  message: string;
}

function getReplayError(error: unknown): ReplayError {
  const responseData = (
    error as {
      response?: {
        data?: {
          error?: unknown;
          message?: unknown;
        };
      };
    }
  )?.response?.data;
  const code = typeof responseData?.error === "string" ? responseData.error : null;
  if (code === REQUISITION_REJECTED_CONFLICT) {
    return {
      code,
      message:
        typeof responseData?.message === "string" && responseData.message.trim()
          ? responseData.message
          : REQUISITION_REJECTED_MESSAGE,
    };
  }

  if (error instanceof Error && error.message) {
    return { code, message: error.message };
  }

  return { code, message: "Unknown replay error" };
}

async function markIntent(
  intent: OfflineQueueRecord,
  status: OfflineQueueRecord["status"],
  options: {
    lastError?: string | null;
    lastErrorCode?: string | null;
    serverRequisitionId?: string | null;
  } = {},
): Promise<OfflineQueueRecord> {
  const nextIntent: OfflineQueueRecord = {
    ...intent,
    status,
    serverRequisitionId: options.serverRequisitionId ?? intent.serverRequisitionId,
    lastError: options.lastError ?? null,
    lastErrorCode: options.lastErrorCode ?? null,
    updatedAt: new Date().toISOString(),
  };

  await updateQueuedIntent(nextIntent);
  return nextIntent;
}

async function resolveServerRequisitionId(
  intent: OfflineQueueRecord,
  resolvedServerIds: Map<string, string>,
): Promise<string | null> {
  if (intent.serverRequisitionId) {
    return intent.serverRequisitionId;
  }

  const resolved = resolvedServerIds.get(intent.localDraftId);
  if (resolved) {
    return resolved;
  }

  const draft = await getDraft(intent.localDraftId);
  return draft?.serverRequisitionId ?? null;
}

export async function replayPendingRequisitionIntents(): Promise<ReplayResult> {
  const pendingIntents = (await listQueuedIntents()).filter(
    (intent) =>
      intent.status === "pending" ||
      intent.status === "replaying" ||
      (intent.status === "failed" &&
        intent.lastErrorCode !== REQUISITION_REJECTED_CONFLICT &&
        intent.lastErrorCode !== OFFLINE_UUID_MIGRATION_CONFLICT),
  );
  const resolvedServerIds = new Map<string, string>();
  const resolvedCommentIds = new Map<string, string>();
  let processedCount = 0;

  for (const intent of pendingIntents) {
    const currentIntent = (await getQueuedIntent(intent.id)) ?? intent;
    const replayingIntent = await markIntent(currentIntent, "replaying");

    try {
      if (replayingIntent.intentType === "create_draft") {
        const created = await createRequisition(replayingIntent.payload);
        const serverRequisitionId = await rewriteOfflineRequisitionServerReference(
          replayingIntent.localDraftId,
          created.data.id,
        );
        resolvedServerIds.set(replayingIntent.localDraftId, serverRequisitionId);

        await markIntent(replayingIntent, "completed", { serverRequisitionId });
      }

      if (replayingIntent.intentType === "update_draft") {
        const serverRequisitionId = await resolveServerRequisitionId(replayingIntent, resolvedServerIds);
        if (!serverRequisitionId) {
          throw new Error("Missing server requisition mapping");
        }

        await updateRequisition(serverRequisitionId, replayingIntent.payload);
        await markIntent(replayingIntent, "completed", { serverRequisitionId });
      }

      if (replayingIntent.intentType === "submit_draft") {
        const serverRequisitionId = await resolveServerRequisitionId(replayingIntent, resolvedServerIds);
        if (!serverRequisitionId) {
          throw new Error("Missing server requisition mapping");
        }

        await submitRequisition(serverRequisitionId);
        await markIntent(replayingIntent, "completed", { serverRequisitionId });
      }

      if (replayingIntent.intentType === "create_comment") {
        const serverRequisitionId = await resolveServerRequisitionId(replayingIntent, resolvedServerIds);
        if (!serverRequisitionId) {
          throw new Error("Missing server requisition mapping");
        }

        const resolvedParentId =
          replayingIntent.payload.parent_id && resolvedCommentIds.has(replayingIntent.payload.parent_id)
            ? resolvedCommentIds.get(replayingIntent.payload.parent_id) ?? null
            : replayingIntent.payload.parent_id ?? null;

        const created = await createRequisitionComment(serverRequisitionId, {
          text: replayingIntent.payload.text,
          parent_id: resolvedParentId,
          client_comment_id: replayingIntent.payload.client_comment_id,
          ...(replayingIntent.payload.mentioned_recipients?.length
            ? { mentioned_recipients: replayingIntent.payload.mentioned_recipients }
            : {}),
        });
        resolvedCommentIds.set(replayingIntent.payload.client_comment_id, normalizeUuid(created.data.id));
        await markIntent(replayingIntent, "completed", { serverRequisitionId });
      }

      processedCount += 1;
    } catch (error) {
      const replayError = getReplayError(error);
      await markIntent(replayingIntent, "failed", {
        lastError: replayError.message,
        lastErrorCode: replayError.code,
      });
      return {
        processedCount,
        failedIntentId: replayingIntent.id,
      };
    }
  }

  if (processedCount > 0) {
    await recordLastSyncedAt(new Date().toISOString());
  }

  return {
    processedCount,
    failedIntentId: null,
  };
}
